import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { trackViewContent } from "../utils/metaTracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MocktailsPage = () => {
  const [mocktails, setMocktails] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [mocktailsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/mocktails`),
          axios.get(`${API}/mocktails/categories`),
        ]);
        setMocktails(mocktailsRes.data);
        setCategories(["All", ...categoriesRes.data.categories]);
      } catch (error) {
        console.error("Error fetching mocktails:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Meta: ViewContent for mocktails page
    trackViewContent({ slug: 'mocktails', title: 'Mocktails Menu', businessLine: 'mocktails' });
  }, []);

  const filteredMocktails = activeCategory === "All"
    ? mocktails
    : mocktails.filter(m => m.category === activeCategory);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <main className="pt-20" data-testid="mocktails-page">
      {/* Hero */}
      <section className="section-padding bg-[#050505]">
        <div className="container-luxury">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4">
              The Menu
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold mb-6 text-[#F5F5F0]">
              Signature Mocktails
            </h1>
            <p className="text-[#A1A1AA] text-lg leading-relaxed">
              Each creation is a work of art—crafted with premium ingredients, 
              innovative techniques, and a passion for perfection.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#0F0F0F] border-y border-[rgba(212,175,55,0.15)] sticky top-20 z-40">
        <div className="container-luxury">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 text-sm uppercase tracking-widest transition-all ${
                  activeCategory === category
                    ? "bg-[#D4AF37] text-[#050505]"
                    : "border border-[rgba(212,175,55,0.3)] text-[#A1A1AA] hover:border-[#D4AF37] hover:text-[#D4AF37]"
                }`}
                data-testid={`category-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Mocktails Grid */}
      <section className="section-padding bg-[#050505]" data-testid="mocktails-grid">
        <div className="container-luxury">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="menu-grid">
              {filteredMocktails.map((mocktail, index) => (
                <motion.div
                  key={mocktail.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  className="group"
                  data-testid={`mocktail-${mocktail.id}`}
                >
                  <div className="aspect-[3/4] overflow-hidden mb-6 luxury-card">
                    <img
                      src={mocktail.image_url}
                      alt={mocktail.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-1">
                        {mocktail.category}
                      </p>
                      <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#F5F5F0] group-hover:text-[#D4AF37] transition-colors">
                        {mocktail.name}
                      </h3>
                    </div>
                    <span className="font-['Playfair_Display'] text-xl font-semibold gold-text">
                      ${mocktail.price.toFixed(0)}
                    </span>
                  </div>
                  
                  <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">
                    {mocktail.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    {mocktail.ingredients.slice(0, 3).map((ingredient, i) => (
                      <span
                        key={i}
                        className="text-xs px-3 py-1 border border-[rgba(212,175,55,0.2)] text-[#A1A1AA]"
                      >
                        {ingredient}
                      </span>
                    ))}
                    {mocktail.ingredients.length > 3 && (
                      <span className="text-xs px-3 py-1 text-[#D4AF37]">
                        +{mocktail.ingredients.length - 3} more
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Custom Cocktails CTA */}
      <section className="section-padding bg-[#0F0F0F]" data-testid="custom-cta">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="aspect-[4/3] overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1767065702505-b6e57a0734b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxjb2xvcmZ1bCUyMGNvY2t0YWlsJTIwZHJpbmslMjBkYXJrJTIwYmFja2dyb3VuZCUyMHByb2Zlc3Npb25hbCUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc3MjM1MzYwMHww&ixlib=rb-4.1.0&q=85"
                alt="Custom mocktail creation"
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4">
                Bespoke Creations
              </p>
              <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-6 text-[#F5F5F0]">
                Custom Mocktail Design
              </h2>
              <div className="gold-divider" />
              <p className="text-[#A1A1AA] leading-relaxed mb-6">
                Want something unique for your event? Our mixologists can create 
                custom recipes tailored to your theme, color palette, and flavor preferences.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Personalized naming for your signature drinks",
                  "Color matching to your event theme",
                  "Dietary accommodations (sugar-free, vegan)",
                  "Interactive tasting sessions"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#A1A1AA]">
                    <span className="text-[#D4AF37]">—</span>
                    {item}
                  </li>
                ))}
              </ul>
              <a 
                href="/contact" 
                className="btn-primary inline-block"
                data-testid="custom-inquiry-btn"
              >
                Inquire About Custom Recipes
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MocktailsPage;
